// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------
import React, { useState, useEffect } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useDispatch, useSelector } from "react-redux";
import { Default as Theme } from "themes/default";
// -------------------------------------------------------------------------------
// Store
// -------------------------------------------------------------------------------
import { selectDomainGym, selectLoginLoading, selectAuthToken } from "store/selectors";
import { autoLogin } from "store/slices";

// -----------------------------------------------------------------------------
// Components and helpers
// -----------------------------------------------------------------------------
import { userAtom } from "../../globalAtoms";
import LoginForm from "./LoginForm";
import SignUpForm from "./SignUpForm";
import Logo from "../../images/logo.svg";
import LoginImage from "../../images/login-image.png";
import { useGetDomainGymQuery, useLazyGetPagesQuery, useStripeGetGymProductQuery } from "store/api/api";
import { LoadingMask } from "components/Common/LoadingMask";
import { useLoginProgress } from "utils/loginInProgress";
import { Flex } from "antd";
// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const FormHeader = ({ type, page, subText }) => {
	let headerText = type === "signup" ? "Create Your Account" : "Login";
	let subheaderText = type === "signup" ? "Enter your info below" : "Enter your login information below";

	if (page?.sub_text) {
		subheaderText = page.sub_text;
		headerText = page.header_text;
	}

	return (
		<div className="sb-login__container__form-container__header">
			<Link to="/">
				<img className="sb-login__container__form-container__header__image" src={Logo} alt="logo" />
			</Link>
			<h1 className="sb-login__container__form-container__header__title" data-id="lblLoginGymTitle">
				{headerText}
			</h1>
			<p className="sb-login__container__form-container__header__subtitle">{subText || subheaderText}</p>
		</div>
	);
};

const LoginSection = ({ type, page, paymentsReady, preview, gymId, headerImage }) => {
	const [text, setText] = useState("");

	return (
		<div className="sb-login__container">
			<div style={{ ...headerImage }} className="sb-login__container__form-container">
				<FormHeader subText={text} type={type} page={page} />
				{type === "signup" ? (
					paymentsReady ? (
						<SignUpForm preview={preview} />
					) : (
						<SignUpForm preview={true} />
					)
				) : (
					<LoginForm onLoginSuccess={() => setText("Verify Your Phone Number")} preview={preview} gymId={gymId} />
				)}
			</div>
			<Flex style={{ ...headerImage, minWidth: `${(window.innerHeight * 9) / 16}px` }} className="signup-image">
				<div />
			</Flex>
		</div>
	);
};

const Login = ({ preview, type }) => {
	const location = useLocation();
	const dispatch = useDispatch();
	const [pages, setPages] = useState(null);
	const [gymId] = useState(null);
	const [paymentsReady, setPaymentsReady] = useState(true);
	let { gym } = useParams();
	const { data: domainGymData, isLoading: domainGymIsLoading, error: domainGymError } = useGetDomainGymQuery(gym);
	const { data: gymProduct, isLoading: gymProductIsLoading } = useStripeGetGymProductQuery(
		{ gymId: domainGymData?.id },
		{ skip: !domainGymData?.id }
	);
	const domainGym = useSelector(selectDomainGym);
	const [trigger, result, lastPromiseInfo] = useLazyGetPagesQuery({});
	const { inProgress } = useLoginProgress();

	const loginLoading = useSelector(selectLoginLoading);
	const authToken = useSelector(selectAuthToken);
	const user = useRecoilValue(userAtom);
	useEffect(() => {
		if (!user && domainGym?.autologin && !loginLoading) {
			dispatch(autoLogin({ gym_id: domainGym.id }));
		}
	}, [user, domainGym, dispatch, loginLoading]);

	useEffect(() => {
		if (result?.data) {
			setPages(result?.data);
		}
	}, [trigger, result, lastPromiseInfo, domainGymData?.id]);

	useEffect(() => {
		if (!loginLoading && authToken) {
			localStorage.setItem("creator_jwt_token", authToken);
			window.location = window.location.origin + "/dashboard";
		}
	}, [loginLoading, authToken]);

	const fetchPages = async (gymID) => {
		await trigger(gymID).unwrap();
	};
	useEffect(() => {
		if (domainGymData?.id) {
			if (gymProduct?.unit_amount >= 0 && gymProduct?.transfers_enabled) {
				setPaymentsReady(true);
			} else {
				setPaymentsReady(false);
			}
			fetchPages(domainGymData?.id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [domainGymData, gymProduct]);

	const page = user ? user.pages?.find((p) => p.type === "sign_up") : pages?.find((p) => p.type === "sign_up");
	const headerImage = {
		backgroundImage: `url(${page?.header_image || LoginImage})`,
	};

	if (location.search.includes("preview")) {
		preview = true;
	}
	if (user && !preview) {
		return <Navigate to={`/${gym}/dashboard`} />;
	}

	if (domainGymIsLoading || gymProductIsLoading) {
		return <LoadingMask />;
	}

	if (!domainGymData?.id || domainGymError) {
		return <Navigate to="/" />;
	}

	return (
		<Theme>
			<div className="sb-login">
				<LoginSection
					inProgress={inProgress}
					type={type}
					page={page}
					paymentsReady={paymentsReady}
					preview={preview}
					gymId={gymId}
					headerImage={headerImage}
				/>
			</div>
		</Theme>
	);
};

export default Login;
